import {
    ChatBubbleLeftRightIcon,
    CheckIcon,
    ClockIcon,
    EllipsisHorizontalIcon,
    EnvelopeIcon,
    MapPinIcon,
    PhoneArrowUpRightIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import {
    ActivityParentEntityId,
    PhoneNumberE164,
    PlaybookTouchpointId,
    ReferenceContactId,
    RegionId,
    TouchpointKind,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { PhoneCall } from "components/telecom/phone-call";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import {
    PrimaryButton,
    primaryClasses,
    primaryCompactClasses,
} from "components/ui/primary-button";
import {
    SecondaryButton,
    SecondaryCompactButton,
    secondaryCompactClasses,
} from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { TextArea } from "components/ui/textarea";
import { VisitEditor } from "components/visits/visit-editor";
import { addMonths, addWeeks } from "date-fns";
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
} from "lib/headlessui/components/menu/menu";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { EmailActionState } from "./marketing-plan-agenda";
import { channelLabel } from "./marketing-plan-format";
import { useAgendaInvalidation } from "./use-agenda-invalidation";

const todayDateString = (): string => new Date().toISOString().slice(0, 10);

export const contactPath = (contactId: string): string =>
    `/references/contacts/show/${contactId}`;

const provisionPath = "/settings/account/phone";

const iconClass = "h-3.5 w-3.5 flex-shrink-0";

const ButtonLink: React.FC<{
    to: string;
    variant: "primary" | "secondary";
    children: React.ReactNode;
}> = ({ to, variant, children }) => (
    <Link
        to={to}
        className={`items-center gap-1 ${
            variant === "primary" ? primaryCompactClasses : secondaryCompactClasses
        }`}>
        {children}
    </Link>
);

export const AgendaEmailAction: React.FC<{
    state: EmailActionState;
    contactId: string;
    onSendEmail: () => void;
}> = ({ state, contactId, onSendEmail }) => {
    if (state === "email-opted-out") {
        return (
            <span className="self-center text-xs italic text-gray-400">
                Opted out of email
            </span>
        );
    }
    if (state === "add-email") {
        return (
            <ButtonLink to={contactPath(contactId)} variant="secondary">
                <PlusIcon className={iconClass} />
                Add email
            </ButtonLink>
        );
    }
    return (
        <SecondaryCompactButton onClick={onSendEmail} className="items-center gap-1">
            <EnvelopeIcon className={iconClass} />
            Send email
        </SecondaryCompactButton>
    );
};

export const AgendaAddContactPhone: React.FC<{ contactId: string }> = ({ contactId }) => (
    <ButtonLink to={contactPath(contactId)} variant="secondary">
        <PlusIcon className={iconClass} />
        Add phone number
    </ButtonLink>
);

export const AgendaProvisionPhone: React.FC<unknown> = () => (
    <ButtonLink to={provisionPath} variant="secondary">
        <PhoneArrowUpRightIcon className={iconClass} />
        Set up calling
    </ButtonLink>
);

export const AgendaCallAction: React.FC<{
    phone: PhoneNumberE164;
    entityId: ActivityParentEntityId;
}> = ({ phone, entityId }) => {
    const invalidateAgenda = useAgendaInvalidation();
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => setIsOpen(true)}>
                <PhoneArrowUpRightIcon className={iconClass} />
                Call
            </SecondaryCompactButton>
            <Panel
                isOpen={isOpen}
                onDismiss={() => {
                    setIsOpen(false);
                    invalidateAgenda();
                }}
                type={PanelType.medium}
                headerText="Phone Call"
                hasCloseButton={true}>
                <PhoneCall phoneNumber={phone} entityId={entityId} />
            </Panel>
        </>
    );
};

export const AgendaTextAction: React.FC<{ phone: PhoneNumberE164 }> = ({ phone }) => {
    const invalidateAgenda = useAgendaInvalidation();
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => setIsOpen(true)}>
                <ChatBubbleLeftRightIcon className={iconClass} />
                Text
            </SecondaryCompactButton>
            <SendSmsPanel
                isOpen={isOpen}
                onDismiss={() => {
                    setIsOpen(false);
                    invalidateAgenda();
                }}
                recipient={phone}
            />
        </>
    );
};

export const AgendaVisitAction: React.FC<{
    contactId: string;
    contactName: string;
}> = ({ contactId, contactName }) => {
    const invalidateAgenda = useAgendaInvalidation();
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <SecondaryCompactButton
                className="items-center gap-1"
                onClick={() => setIsOpen(true)}>
                <MapPinIcon className={iconClass} />
                Schedule visit
            </SecondaryCompactButton>
            {isOpen ? (
                <VisitEditor
                    mode="new"
                    parent={{
                        kind: "contact",
                        id: contactId as ReferenceContactId,
                        name: contactName,
                        address: null,
                    }}
                    onSaved={invalidateAgenda}
                    onClose={() => setIsOpen(false)}
                />
            ) : null}
        </>
    );
};

const MarkDonePanel: React.FC<{
    contactId: string;
    kinds: string[];
    isOpen: boolean;
    onClose: () => void;
}> = ({ contactId, kinds, isOpen, onClose }) => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const invalidateAgenda = useAgendaInvalidation();
    const [kind, setKind] = useState(kinds[0] ?? "email");
    const [occurredOn, setOccurredOn] = useState(todayDateString());
    const [note, setNote] = useState("");

    const logMutation = useMutation({
        ...trpcClient.marketingPlaybook.logTouchpoint.mutationOptions(),
        onSuccess: () => {
            handleEvent("marketing-plan-mark-done", { channel: kind });
            onClose();
            invalidateAgenda();
        },
    });

    const save = () => {
        logMutation.mutate({
            regionId: regionId as RegionId,
            contactId: contactId as ReferenceContactId,
            kinds: [kind as TouchpointKind],
            occurredAt: occurredOn,
            note: note.trim() ? note.trim() : null,
        });
    };

    return (
        <>
            <Panel
                isOpen={isOpen}
                onDismiss={onClose}
                type={PanelType.medium}
                headerText="Mark touchpoint done"
                hasCloseButton={true}>
                <div className="space-y-4">
                    <p className="text-sm text-gray-500">
                        Already reached out on your own? Record it here so this touchpoint
                        counts — no email or calendar event is created.
                    </p>
                    <Select
                        label="How did you reach them?"
                        value={kind}
                        onChange={e => setKind(e.target.value)}>
                        {kinds.map(k => (
                            <option key={k} value={k}>
                                {channelLabel(k)}
                            </option>
                        ))}
                    </Select>
                    <Input
                        label="When"
                        type="date"
                        value={occurredOn}
                        onChange={e => setOccurredOn(e.target.value)}
                    />
                    <TextArea
                        label="Note (optional)"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <PrimaryButton onClick={save} disabled={logMutation.isPending}>
                            Mark done
                        </PrimaryButton>
                    </div>
                </div>
            </Panel>
        </>
    );
};

type SnoozePreset = "skipOnce" | "1week" | "1month" | "3months" | "custom";

const presetSnoozeUntil = (preset: SnoozePreset, customDate: string): Date | null => {
    switch (preset) {
        case "1week":
            return addWeeks(new Date(), 1);
        case "1month":
            return addMonths(new Date(), 1);
        case "3months":
            return addMonths(new Date(), 3);
        case "custom":
            return customDate ? new Date(customDate) : null;
        case "skipOnce":
            return null;
    }
};

const SnoozePanel: React.FC<{
    contactId: string;
    touchpointId: string | null;
    isOpen: boolean;
    onClose: () => void;
}> = ({ contactId, touchpointId, isOpen, onClose }) => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const invalidateAgenda = useAgendaInvalidation();
    const [reason, setReason] = useState("");
    const [customDate, setCustomDate] = useState("");
    const [preset, setPreset] = useState<SnoozePreset | null>(null);

    const snoozeMutation = useMutation({
        ...trpcClient.marketingPlaybook.snooze.mutationOptions(),
        onSuccess: () => {
            handleEvent("marketing-plan-snooze", {
                scope: touchpointId ? "touchpoint" : "contact",
            });
            onClose();
            invalidateAgenda();
        },
    });

    const resolveIntent = ():
        | { kind: "skipOnce" }
        | { kind: "until"; snoozeUntil: string }
        | null => {
        if (preset === null) {
            return null;
        }
        if (preset === "skipOnce") {
            return { kind: "skipOnce" };
        }
        const date = presetSnoozeUntil(preset, customDate);
        return date ? { kind: "until", snoozeUntil: date.toISOString() } : null;
    };

    const intent = resolveIntent();

    const submit = () => {
        if (!intent) {
            return;
        }
        snoozeMutation.mutate({
            regionId: regionId as RegionId,
            contactId: contactId as ReferenceContactId,
            touchpointId: touchpointId ? (touchpointId as PlaybookTouchpointId) : null,
            intent,
            reason: reason.trim() ? reason.trim() : null,
        });
    };

    const presetOptions: { key: SnoozePreset; label: string }[] = [];
    if (touchpointId) {
        presetOptions.push({ key: "skipOnce", label: "Skip once" });
    }
    presetOptions.push({ key: "1week", label: "1 week" });
    presetOptions.push({ key: "1month", label: "1 month" });
    presetOptions.push({ key: "3months", label: "3 months" });

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onClose}
            type={PanelType.medium}
            headerText={touchpointId ? "Snooze touchpoint" : "Snooze all outreach"}
            hasCloseButton={true}>
            <div className="space-y-4">
                <TextArea
                    label="Reason (optional)"
                    value={reason}
                    placeholder="e.g. on maternity leave until the fall"
                    onChange={e => setReason(e.target.value)}
                />
                <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-700">Snooze for</span>
                    <div className="flex flex-wrap gap-2">
                        {presetOptions.map(option =>
                            preset === option.key ? (
                                <PrimaryButton
                                    key={option.key}
                                    onClick={() => setPreset(option.key)}>
                                    {option.label}
                                </PrimaryButton>
                            ) : (
                                <SecondaryButton
                                    key={option.key}
                                    onClick={() => setPreset(option.key)}>
                                    {option.label}
                                </SecondaryButton>
                            ),
                        )}
                    </div>
                </div>
                <div className="border-t border-gray-100 pt-3">
                    <Input
                        label="Or until a specific date"
                        type="date"
                        value={customDate}
                        onChange={e => {
                            setCustomDate(e.target.value);
                            setPreset(e.target.value ? "custom" : null);
                        }}
                    />
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton
                        disabled={!intent || snoozeMutation.isPending}
                        onClick={submit}>
                        Snooze
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};

const overflowItemClasses =
    "flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-brand-100 hover:text-brand-900";

export const AgendaTouchpointOverflowMenu: React.FC<{
    contactId: string;
    touchpointId: string;
    kinds: string[];
}> = ({ contactId, touchpointId, kinds }) => {
    const [markDoneOpen, setMarkDoneOpen] = useState(false);
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    return (
        <>
            <Menu as="div" className="relative inline-block text-left">
                <MenuButton
                    className={`items-center ${secondaryCompactClasses}`}
                    aria-label="More actions">
                    <EllipsisHorizontalIcon className="h-4 w-4" />
                </MenuButton>
                <MenuItems className="z-1300 absolute right-0 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-200 focus:outline-none">
                    <div className="py-1">
                        <MenuItem>
                            <button
                                type="button"
                                className={overflowItemClasses}
                                onClick={() => setMarkDoneOpen(true)}>
                                <CheckIcon className={iconClass} />
                                Mark done
                            </button>
                        </MenuItem>
                        <MenuItem>
                            <button
                                type="button"
                                className={overflowItemClasses}
                                onClick={() => setSnoozeOpen(true)}>
                                <ClockIcon className={iconClass} />
                                Snooze
                            </button>
                        </MenuItem>
                    </div>
                </MenuItems>
            </Menu>
            <MarkDonePanel
                contactId={contactId}
                kinds={kinds}
                isOpen={markDoneOpen}
                onClose={() => setMarkDoneOpen(false)}
            />
            <SnoozePanel
                contactId={contactId}
                touchpointId={touchpointId}
                isOpen={snoozeOpen}
                onClose={() => setSnoozeOpen(false)}
            />
        </>
    );
};

export const AgendaProvisionBanner: React.FC<unknown> = () => (
    <InlineBanner type="info">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
                Set up a phone number to call and text your referral sources right from My
                Agenda.
            </span>
            <Link
                to={provisionPath}
                className={`items-center whitespace-nowrap ${primaryClasses}`}>
                <PhoneArrowUpRightIcon className="mr-1.5 h-4 w-4 flex-shrink-0" />
                Set up phone number
            </Link>
        </div>
    </InlineBanner>
);
